import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import {
  assertOneOfStatus,
  assertStatus,
  extractTotalCountHeader,
} from "@mittwald/api-client-NEXT";
import { mittwaldApi } from "../../../../api/Mittwald";
import { checkForValidationErrors } from "../../../base/index.js";
import type { CustomerCreateRequestData } from "../types.js";
import type { CustomerBehaviors } from "./types.js";

export const apiCustomerBehaviors = (
  client: MittwaldAPIV2Client,
): CustomerBehaviors => ({
  find: async (customerId) => {
    const response = await client.customer.getCustomer({ customerId });

    if (response.status === 200) {
      return response.data;
    }
    assertOneOfStatus(response, [403, 404, 401]);
  },

  list: async (query) => {
    const response = await client.customer.listCustomers({
      queryParameters: query,
    });
    assertStatus(response, 200);
    return {
      items: response.data,
      totalCount: extractTotalCountHeader(response),
    };
  },

  create: async (data: CustomerCreateRequestData) => {
    const response = await client.customer.createCustomer({
      data,
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 201);
    return { id: response.data.customerId };
  },

  update: async (customerId, data) => {
    const response = await client.customer.updateCustomer({
      customerId,
      data: { ...data, customerId },
    });

    const validationErrors = checkForValidationErrors(response);

    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 200);
  },

  delete: async (customerId) => {
    const response = await client.customer.deleteCustomer({
      customerId,
    });
    assertStatus(response, 200);
  },

  createAvatarUploadToken: async (customerId) => {
    const response = await client.customer.requestAvatarUpload({
      customerId,
    });
    assertStatus(response, 200);
    return {
      token: response.data.refId,
      rules: response.data.rules,
    };
  },

  removeAvatar: async (customerId) => {
    const response = await client.customer.removeAvatar({ customerId });

    assertStatus(response, 204);
  },

  createRecommendationSuggestion: async (customerId, suggestion) => {
    const response = await client.customer.createRecommendationSuggestion({
      customerId,
      data: { suggestion },
    });

    assertStatus(response, 404);
  },

  findPaymentMethod: async (customerId) => {
    // todo: replace when available in new client
    const response =
      await mittwaldApi.marketplaceCustomerGetPaymentMethod.request({
        path: { customerId },
      });

    if (response.status === 200) {
      return response.content;
    }

    //assertStatus(response, 404);
  },

  updatePaymentMethod: async (customerId, customReturnUrl) => {
    // todo: replace when available in new client

    const response =
      await mittwaldApi.marketplaceCustomerUpdatePaymentMethod.request({
        path: { customerId },
        requestBody: { customReturnUrl },
      });

    //assertStatus(response, 200);

    if (response.status === 200) {
      return response.content.url;
    }
  },

  getBillingPortalLink: async (customerId) => {
    // todo: replace when available in new client
    const response =
      await mittwaldApi.contributorGetCustomerBillingPortalLink.request({
        path: { customerId },
      });

    //assertStatus(response, 200);

    if (response.status === 200) {
      return response.content.url;
    }
  },
});
